import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HomeSummary, SiteSummary } from "../../shared/types";
import { StaticDataState } from "../components/StaticDataState";
import { formatCourseOfferingPreview } from "../lib/offerings";
import { fetchHomeSummary, fetchSiteSummary } from "../lib/static-data";

export function HomePage() {
  const [summary, setSummary] = useState<SiteSummary | null>(null);
  const [directory, setDirectory] = useState<HomeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setError(null);
    void Promise.all([fetchSiteSummary(), fetchHomeSummary()])
      .then(([nextSummary, nextDirectory]) => {
        setSummary(nextSummary);
        setDirectory(nextDirectory);
      })
      .catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : "Could not load the catalog.");
      });
  }, [reloadKey]);

  if (error) {
    return <StaticDataState title="The catalog could not load." message={error} error onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  if (!summary || !directory) {
    return <StaticDataState title="Loading the catalog..." />;
  }

  return (
    <div className="home-page">
      <section className="home-hero">
        <div className="home-hero-copy">
          <h1>Choose classes with more context.</h1>
          <p className="home-hero-lede">
            Explore course history, read what students have said over the years, and add your own take before the next
            registration rush.
          </p>
          <div className="home-hero-actions">
            <Link to="/search/" className="primary-link home-hero-primary">
              Search courses
            </Link>
            <Link to="/professors/" className="secondary-link home-hero-link">
              Search professors
            </Link>
            <Link to="/departments/" className="secondary-link home-hero-link">
              Browse departments
            </Link>
          </div>
        </div>
        <aside className="home-benefits" aria-label="About Layup List">
          <article>
            <h2>Built for course shopping</h2>
            <p>Find classes quickly, compare departments, and jump straight into the courses students talk about most.</p>
          </article>
          <article>
            <h2>Dartmouth-only accounts</h2>
            <p>New reviews stay behind verified Dartmouth email login, so the conversation stays campus-rooted.</p>
          </article>
          <article>
            <h2>Focused on what matters</h2>
            <p>
              No weird AI analysis, no clutter, no noisy extras. Just course context, professor history, and honest
              student reviews.
            </p>
          </article>
        </aside>
        <dl className="home-facts" aria-label="Layup List catalog totals">
          <div>
            <dt>{summary?.legacyReviewCount ?? "..."}</dt>
            <dd>student reviews</dd>
          </div>
          <div>
            <dt>{summary?.courseCount ?? "..."}</dt>
            <dd>courses</dd>
          </div>
          <div>
            <dt>{summary?.departmentCount ?? "..."}</dt>
            <dd>departments</dd>
          </div>
        </dl>
      </section>

      <section className="home-directory">
        <header className="home-section-heading">
          <div>
            <p className="eyebrow">Explore</p>
            <h2>Browse the catalog</h2>
          </div>
          <p>Start with a department or jump into the courses with the most student reviews.</p>
        </header>

        <div className="home-directory-grid">
          <article className="home-directory-block">
            <div className="home-directory-heading">
              <h3>Popular departments</h3>
              <Link to="/departments/">View all</Link>
            </div>
            <div className="home-directory-list">
              {directory.departments.map((department) => (
                <Link key={department.code} className="home-directory-row" to={`/departments/${department.code}/`}>
                  <strong>{department.code}</strong>
                  <span>{department.name || "Department listing"}</span>
                  <small>{department.courseCount} courses</small>
                </Link>
              ))}
            </div>
          </article>

          <article className="home-directory-block home-directory-block-wide">
            <div className="home-directory-heading">
              <h3>Top 5 most reviewed courses</h3>
              <Link to="/search/">Search all</Link>
            </div>
            <div className="home-directory-list">
              {directory.spotlight.map((course) => {
                const offeringPreview = formatCourseOfferingPreview(course);

                return (
                  <Link key={course.slug} className="home-directory-row" to={`/course/${course.slug}/`}>
                    <strong>{course.shortName}</strong>
                    <span>{course.title}</span>
                    <small>
                      {course.legacyReviewCount} reviews
                      {offeringPreview ? ` · ${offeringPreview}` : ""}
                    </small>
                  </Link>
                );
              })}
            </div>
          </article>
        </div>
      </section>

    </div>
  );
}
